/**
 * Where the write-up's images and models live.
 *
 * Uploaded to the Magic Patterns CDN. Each upload gets its own directory, so
 * there is no shared prefix to append a filename to — the mapping has to be
 * explicit. That is the only reason this is a table rather than a base URL.
 *
 * The originals are in the project repo under `docs/assets/` at the same byte
 * sizes, so these can be re-uploaded or moved to another host by replacing the
 * right-hand side here. Nothing else references a URL.
 *
 * An empty string is a supported value: the figure renders as a dashed box
 * saying the image is pending, with its real alt text and caption already in
 * place, so an entry can be written before its asset is hosted.
 */
const uploads: Record<string, string> = {
  'stylize.jpg':
    'https://cdn.magicpatterns.com/uploads/dkJSnrNx1azaDrfF4y77uf/stylize.jpg',
  'model.jpg':
    'https://cdn.magicpatterns.com/uploads/eNsQi15ziM9AFKDiM3zLFx/model.jpg',
  'mesh.jpg':
    'https://cdn.magicpatterns.com/uploads/qnEhRNyQKAE8LoQ3ksT1vH/mesh.jpg',
  'rig.jpg':
    'https://cdn.magicpatterns.com/uploads/uifaWKQpPArE1xi9wMaiUW/rig.jpg',
  'walk.jpg':
    'https://cdn.magicpatterns.com/uploads/fLL1ZZSwixc1L5HrHiytHG/walk.jpg',
  'drift.jpg':
    'https://cdn.magicpatterns.com/uploads/ncvGpRobN4GGZDhz2kczdJ/drift.jpg',
  'portrait.jpg':
    'https://cdn.magicpatterns.com/uploads/nYZWYmmuWHCPjazpA5j77f/portrait.jpg',
  // Rebuilt for the write-up by tools/blender/ghost_face.py, since the original
  // failure renders were not kept. In the repo at docs/assets/skinned.jpg.
  'skinned.jpg':
    'https://cdn.magicpatterns.com/uploads/8zvHYN7uRSpfeJ2XtzVHWy/skinned.jpg',
}

export const asset = (name: string) => uploads[name] ?? ''

/**
 * The rigged, animated characters themselves.
 *
 * Served from the same bucket as the images, and verified byte-identical to the
 * repo copies on the way in. They come back as `model/gltf-binary` with
 * `access-control-allow-origin: *`, which is what makes them loadable from here.
 *
 * `docs/assets/ted.glb` and `alex.glb` in the project repo, about 1.9MB each,
 * down from 22 and 29MB — textures to WebP at half resolution (4096² to 2048²),
 * geometry through meshopt, and the provider's normal map dropped, since the
 * finish stage had already measured it as noise rather than detail.
 *
 * These are the walk exports, so they carry the 24-joint spec skeleton the clip
 * drives rather than the finished avatar's 40. Anything on the page that quotes
 * a joint count needs to say which of the two it means.
 *
 * Setting either back to an empty string is a supported way to turn the live
 * viewer off: it falls through to the stills below.
 */
export const models: Record<string, string> = {
  ted: 'https://cdn.magicpatterns.com/uploads/va8nskq9xtS4YaPyL3Wt5A/ted.glb',
  alex: 'https://cdn.magicpatterns.com/uploads/4kMiPScKofe7rzi85LHGgM/alex.glb',
}

/**
 * Stills of the same stages, used wherever the model cannot be.
 *
 * Stage 01 is not among them: its output really is three flat images, so it is
 * shown as one rather than pretended into a 3D viewer.
 */
export const pipelineFrames = [
  {
    label: '02',
    title: 'Reconstruct',
    src: asset('model.jpg'),
    alt: 'The reconstructed character as an untextured grey model',
    caption: 'What comes back from multi-image reconstruction, before any material work.',
  },
  {
    label: '03',
    title: 'Mesh',
    src: asset('mesh.jpg'),
    alt: 'The same character with its wireframe drawn over the textured surface',
    caption: 'The topology it actually built, with the baked atlas painted back onto it.',
  },
  {
    label: '04',
    title: 'Rig',
    src: asset('rig.jpg'),
    alt: 'The character rendered translucent so the bound skeleton is visible inside it',
    caption: 'Skin dropped back so the bound skeleton reads, fitted to this character.',
  },
  {
    label: '05',
    title: 'Animate',
    src: asset('walk.jpg'),
    alt: 'The finished character mid-stride in a walk cycle',
    caption: 'A banked clip playing on the result — nothing here was authored for him.',
  },
]
